import { omit, pickBy, mapValues, has, isNull, isUndefined, isObject, isArray, get, castArray  } from "lodash";
import { HttpParams } from "@angular/common/http";

export function camelCaseToSentenceCase (text) {
  var result = text.replace( /([A-Z])/g, " $1" );
  var finalResult = result.charAt(0).toUpperCase() + result.slice(1);
  return finalResult;
}


export function ifDateBetween(check, from, to){
  return ((check.getTime() <= to.getTime() && check.getTime() >= from.getTime()))
}

export function ifDateUpcoming(check, upcoming){
  return (check.getTime() <= upcoming.getTime())
}

export function ifDatePast(check, past){
  return (check.getTime() > past.getTime())
}

export function countDaysBetweenDate(firstDate, secondDate){
  const oneDay = 24 * 60 * 60 * 1000; // hours*minutes*seconds*milliseconds
  return Math.round(Math.abs((firstDate - secondDate) / oneDay))
}

export function getDatesBetweenRange(startDate, endDate, steps = 1) {
  const dateArray = [];
  let currentDate = new Date(startDate);

  while (currentDate <= new Date(endDate)) {
    dateArray.push(new Date(currentDate));
    // Use UTC date to prevent problems with time zones and DST
    currentDate.setUTCDate(currentDate.getUTCDate() + steps);
  }

  return dateArray;
}

export function prepRecordForApi(record): any{
  let changedRecord = omit(record, ['id', 'documentId', 'createdAt', 'creator', 'lastUpdatedOn', 'updatedAt', 'archivedOn', 'archivedBy']);
  // convert related objects to their documentId (or id); handle arrays of objects too 
  // remove any attributes whose key contains a dot
  const keysWithDot = Object.keys(changedRecord).filter(k => k.indexOf('.') !== -1);
  if (keysWithDot.length) {
    changedRecord = omit(changedRecord, keysWithDot);
  }
  changedRecord = pickBy(changedRecord, (val) => !isNull(val) && !isUndefined(val));
  changedRecord = mapValues(changedRecord, (val) => {
    if (!isObject(val)) return val;

    if (isArray(val)) {
      return val.map(item =>
        isObject(item) ? (get(item, 'id') ?? get(item, 'documentId') ?? item) : item
      );
    }
    // Prefer numeric id (for media/file objects) over documentId (for content-type relations)
    return (has(val, 'id') || has(val, 'documentId')) ? (get(val, 'id') ?? get(val, 'documentId')) : val;
  });
  return changedRecord;
}

/**
 * Prepare record for Strapi v5 save: handle relations correctly for REST API.
 * Single relations: send documentId string or null (or numeric id for media).
 * Multiple relations: send array of documentIds.
 * @param record - raw form data with full objects (NOT yet processed by prepRecordForApi)
 * @param entityConfig - config object (e.g. PersonConfig) to identify relation/media fields
 */
export function sanitizeDataForSave(record: any, entityConfig: any): any {
  const sanitized: any = {};

  Object.entries(record).forEach(([key, value]: any) => {
    const fieldConfig = entityConfig?.[key];

    // Skip fields marked as view:false / calculatedField
    if (fieldConfig?.calculatedField) {
      return;
    }

    // Determine if this is a date field
    const isDateField = fieldConfig?.type === 'date' || fieldConfig?.type === 'datetime';
    
    // Skip empty string date fields
    if (isDateField && value === '') {
      return;
    }

    // Determine if this is a relation field
    const isRelation = fieldConfig?.isForeignKey || fieldConfig?.type === 'relation' || fieldConfig?.type === 'file' || fieldConfig?.type === 'media';

    if (isRelation) {
      // File/media fields use numeric id, not documentId
      const isFileField = fieldConfig?.type === 'file' || fieldConfig?.type === 'media';
      // Determine if this is a multi-relation (indicated by multiSelect or array value)
      const isMulti = fieldConfig?.multiSelect || Array.isArray(value);

      if (value === null || value === undefined || value === '' || (isMulti && Array.isArray(value) && value.length === 0)) {
        // Clear the relation
        sanitized[key] = isMulti ? [] : null;
      } else if (isMulti) {
        // Multi-relation: send array of ids (numeric for files, documentId for other relations)
        const ids = (Array.isArray(value) ? value : [value])
          .map((v: any) => {
            if (typeof v === 'number') return v; // already a numeric id
            if (typeof v === 'string') return v; // already a documentId string (fallback)
            if (!isObject(v)) return v;
            // Extract id for files, documentId for content-types
            return isFileField ? (get(v, 'id') ?? get(v, 'documentId')) : (get(v, 'documentId') ?? get(v, 'id'));
          })
          .filter(Boolean);
        sanitized[key] = ids;
      } else {
        // Single relation: send single id (numeric for files, documentId string for other relations)
        if (typeof value === 'number') {
          sanitized[key] = value; // already numeric id
        } else if (typeof value === 'string') {
          sanitized[key] = value; // already documentId string (fallback)
        } else if (isObject(value) && (get(value, 'id') || get(value, 'documentId'))) {
          // Extract id for files, documentId for content-types
          const extracted = isFileField ? (get(value, 'id') ?? get(value, 'documentId')) : (get(value, 'documentId') ?? get(value, 'id'));
          sanitized[key] = extracted;
        } else {
          // fallback: pass as-is
          sanitized[key] = value;
        }
      }
      return;
    }

    if (fieldConfig?.type === 'boolean') {
      sanitized[key] = !!value;
    }
    // Non-relation fields: pass through (skip null/undefined/empty string)
    else if (value !== null && value !== undefined && value !== '') {
      sanitized[key] = value;
    }
  });

  return sanitized;
}

export const defaultDataOmitFields = ['department', 'assignments', 'id', 'documentId', 'createdAt', 'createdBy','creator', 'lastUpdatedOn', 'updatedAt', 'updatedBy', 'archivedOn', 'archivedBy'];

/**
 * Appends a declarative filters object as Strapi v4/v5 bracket-notation query params.
 * Shorthand `{ field: value }` becomes `filters[field][$eq]=value`; operator form
 * `{ field: { $op: value } }` becomes `filters[field][$op]=value`. Field values may nest
 * arbitrarily deep to filter through relations, e.g. `{ role: { name: { $ne: 'applicant' } } }`
 * becomes `filters[role][name][$ne]=applicant`. `$or`/`$and` keys take an array of
 * sub-filter objects, e.g. `{ $or: [{ firstName: { $containsi: 'a' } }] }`.
 */
export function appendFilterParams(params: HttpParams, filters?: { [field: string]: any } | null, prefix: string = 'filters'): HttpParams {
  if (!filters) return params;

  Object.entries(filters).forEach(([key, value]) => {
    const path = `${prefix}[${key}]`;

    if ((key === '$or' || key === '$and') && isArray(value)) {
      value.forEach((subFilter: any, idx: number) => {
        params = appendFilterParams(params, subFilter, `${path}[${idx}]`);
      });
    } else if (key.startsWith('$')) {
      // operator leaf, e.g. { $ne: 'applicant' } or { $in: [...] }
      if (isArray(value)) {
        value.forEach((v: any, idx: number) => { params = params.append(`${path}[${idx}]`, `${v}`); });
      } else {
        params = params.append(path, `${value}`);
      }
    } else if (isObject(value) && !isArray(value)) {
      // nested relation/attribute path - recurse until an operator leaf is reached
      params = appendFilterParams(params, value, path);
    } else {
      params = params.append(`${path}[$eq]`, `${value}`);
    }
  });

  return params;
}

export function ensureArray<T> (v: T | T[] | null | undefined): T[] {
  // lodash.castArray handles null/undefined by returning [null] so filter those out
  return castArray(v).filter((x) => x !== null && x !== undefined) as T[];
}